import { Mui, MuiIcons, DateFns } from "@osu/react-ui";
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { DateRangeFilter, TextFilters, RangeFilter } from '../../Report/customFilters';
import { TOOLTIP_STATUS, TOOLTIPS, STUDENT_REQUEST_ACTIONS, ENUM_FIELD_LABEL_MAPS } from '../../util/constants';
import { addSpacesToCamelCase, buildApplicantDetailUrl, humanizeEnumByField } from "../../util/functions";
import EasyCopy from "../../Common/EasyCopy";
import { ToolbarInternalActionsWithReset } from "../../Common/ToolbarInternalActionsWithReset";

// Fallback cell: humanize a column's raw backend enum value (status, decision, request type, etc.) by
// field. Unmapped fields/values fall through unchanged (see humanizeEnumByField).
function makeEnumCell(field) {
  return ({ cell }) => humanizeEnumByField(field, cell.getValue(), ENUM_FIELD_LABEL_MAPS);
}

export default function ServerSideGrid({
  rows,
  columns,
  resultData,
  isFetching,
  paginationModel,
  setPaginationModel,
  sortModel,
  setSortModel,
  filterModel,
  setFilterModel,
  selectedRows,
  setSelectedRows,
  onRowSelectionChange,
  columnVisibility,
  setColumnVisibility,
  columnOrder,
  setColumnOrder,
  sharedTableStyles,
  theme,
  renderTopToolbarContent,
  pageSizeInput,
  setPageSizeInput,
  nestedFieldColumns,
  resetColumns,
  onRefresh,
  canRefresh = true,
}) {
  const [uniqueOptions, setUniqueOptions] = useState({});
  const [uniqueLoading, setUniqueLoading] = useState({});
  const getUniqueColumnValues = useCallback((columnId) => {
    const providedFilterValues = resultData?.columns?.find?.(column => column.field === columnId)?.filterValues;
    if (providedFilterValues) {
      setUniqueOptions((prev) => ({ ...prev, [columnId]: providedFilterValues }));
      return providedFilterValues;
    }

    setUniqueLoading((prev) => ({ ...prev, [columnId]: true }));
    const values = rows
      .map((row) => row[columnId])
      .filter((v) => v !== undefined && v !== null);
    const unique = Array.from(new Set(values));
    setUniqueOptions((prev) => ({ ...prev, [columnId]: unique }));
    setUniqueLoading((prev) => ({ ...prev, [columnId]: false }));
    return unique;
  }, [rows, resultData]);

  useEffect(() => {
    if (!resultData?.columns) return;
    resultData.columns
      .filter((col) => col.filterType === "autocomplete")
      .forEach((col) => {
        if (!uniqueOptions[col.field]) {
          getUniqueColumnValues(col.field);
        }
      });
  }, [resultData?.columns, rows, getUniqueColumnValues, uniqueOptions]);

  function PendingCell({ row }) {
    const value = row?.original?.isPendingProcessing;
    if (value === true || value === 'Yes') {
      return <MuiIcons.HourglassEmpty fontSize="small" titleAccess="Pending Processing" sx={{ color: 'blue' }} />;
    }
    return <></>;
  }

  const getFilterProps = useCallback((filterType, column) => {
    switch (filterType) {
      case "checkbox":
        return { filterVariant: "checkbox" };
      case "autocomplete":
        return {
          filterVariant: "multi-select",
          filterSelectOptions: uniqueOptions[column.field] || [],
          filterSelectProps: { loading: uniqueLoading[column.field] || false },
        };
      case "date-range":
        return { filterVariant: "range", filterFn: "dateBetween" };
      case "text":
      default:
        return { filterVariant: "text" };
    }
  }, [uniqueOptions, uniqueLoading]);

  function renderNameLink({ row }) {
    const { applicantEmployeeId, programId, term } = row.original || {};
    if (!applicantEmployeeId) return null;
    const studentRequestIsActive = row.original.studentRequestIsActive === "Yes";
    const studentRequestType = row.original.studentRequestType;
    
    let tooltip = null;
    if (studentRequestIsActive) {
      if (studentRequestType === STUDENT_REQUEST_ACTIONS.APPEAL.label) {
        tooltip = TOOLTIPS(TOOLTIP_STATUS.APPEAL_REQUESTED);
      } else if (studentRequestType === STUDENT_REQUEST_ACTIONS.PROGRAM_CHANGE.label) {
        tooltip = TOOLTIPS(TOOLTIP_STATUS.PROGRAM_CHANGE_REQUESTED);
      } else if (studentRequestType === STUDENT_REQUEST_ACTIONS.WITHDRAW.label) {
        tooltip = TOOLTIPS(TOOLTIP_STATUS.WITHDRAW_REQUESTED);
      }
    }
    return (
      <>
      <EasyCopy contents={applicantEmployeeId} />
      <Mui.Link
        component={Link}
        to={buildApplicantDetailUrl(applicantEmployeeId, programId, term)}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'underline' }}
      >
        {applicantEmployeeId}{tooltip}
      </Mui.Link>
      </>
    );
  }

  const setColumnFilterValue = useCallback((columnId, value, filterType = "text") => {
    setFilterModel(prev => {
      const other = prev.filter(f => f.id !== columnId);
      if (!isValidFilter(value, filterType)) { return other; }
      return [...other, { id: columnId, value, operator: getOperatorForFilterType(filterType) }];
    });
  }, [setFilterModel]);

  function getOperatorForFilterType(filterType) {
    switch (filterType) {
      case "autocomplete": return "equals";
      case "text": return "contains";
      case "range": return "between";
      case "checkbox": return "equals";
      case "date": return "dateEquals";
      case "date-range": return "dateBetween";
      default: return "equals";
    }
  }

  function isValidFilter(value, filterType) {
    switch (filterType) {
      case "date-range": { return isValidDate(value[0]) && isValidDate(value[1]); }
      case "range": { return isValidValue(value[0]) && isValidValue(value[1]); }
    }
    return isValidValue(value);
  }

  function isValidDate(dt) { return !isNaN(dt?.getTime?.()); }
  function isValidValue(val) { return typeof val !== "undefined" && val !== null && (typeof val !== "string" || val.length > 0); }

  function getColumnFilterValue(columnId) {
    const filter = filterModel.find((f) => f.id === columnId);
    return filter ? filter.value : [null, null];
  }

  const builtColumns = useMemo(() => {
    if (!resultData?.columns || resultData.columns.length === 0) {
      return [];
    }

    return resultData?.columns
    .filter((column) => !column.field.includes('.'))
    .map((column) => {
        const isApplicantId = column.field === 'applicantEmployeeId';
        const base = {
        id: isApplicantId ? "id" : column.field,
        header: column.headerName,
        accessorKey: column.field,
        minSize: 80,
        size: column.width || undefined,
        enableResizing: true,
        filterType: column.filterType,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: column.field === 'isPendingProcessing' ? PendingCell : (column.renderCell === "renderNameLink" ? renderNameLink : makeEnumCell(column.field)),
        default: column.default,
        detailHeaderName: column.detailHeaderName,
        };

        if (column.filterType === "date-range") {
        base.filterFn = "dateBetween";
        base.Filter = (props) => (
            <DateRangeFilter
            {...props}
            getColumnFilterValue={getColumnFilterValue}
            setColumnFilterValue={setColumnFilterValue}
            filterModel={filterModel}
            />
        );
        } else if (column.filterType === "range") {
        base.filterFn = "between";
        base.Filter = (props) => (
            <RangeFilter
            {...props}
            getColumnFilterValue={getColumnFilterValue}
            setColumnFilterValue={setColumnFilterValue}
            filterModel={filterModel}
            />
        );
        } else if (column.filterType === "text") {
        base.Filter = (props) => (
            <TextFilters
            {...props}
            getColumnFilterValue={getColumnFilterValue}
            setColumnFilterValue={setColumnFilterValue}
            filterType="text"
            filterModel={filterModel}
            />
        );
        } else if (column.filterType === "checkbox" || column.filterType === "autocomplete") {
        Object.assign(base, getFilterProps(column.filterType, column));
        } else {
        base.Filter = (props) => (
            <TextFilters
            {...props}
            getColumnFilterValue={getColumnFilterValue}
            setColumnFilterValue={setColumnFilterValue}
            filterType="text"
            filterModel={filterModel}
            />
        );
        }

        return base;
    });
  }, [resultData, filterModel, setColumnFilterValue, getFilterProps]);

  const renderDetailPanel = useCallback(({ row }) => {
    const parentFields = Object.keys(nestedFieldColumns);
    if (parentFields.length === 0) return null;
    
    const hasNestedData = parentFields.some(parentField => {
      const data = row.original[parentField];
      return Array.isArray(data) && data.length > 0;
    });
    
    if (!hasNestedData) return null;
    
    return (
      <Mui.Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 3,
          padding: 2,
          backgroundColor: theme.palette.mode === 'dark' ? '#444' : '#f5f5f5',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        {parentFields.map(parentField => {
          const data = row.original[parentField];
          if (!Array.isArray(data) || data.length === 0) return null;
          
          const cols = nestedFieldColumns[parentField];
          
          return (
            <Mui.Box key={parentField} sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Mui.Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                {addSpacesToCamelCase(parentField)}:
              </Mui.Typography>
              <Mui.Box sx={{ pl: 2 }}>
                {data.map((item, idx) => (
                  <Mui.Box
                    key={idx}
                    sx={{
                      mb: idx < data.length - 1 ? 1.5 : 0,
                      pb: idx < data.length - 1 ? 1.5 : 0,
                      borderBottom: idx < data.length - 1 ? '1px solid' : 'none',
                      borderColor: theme.palette.mode === 'dark' ? '#555' : '#ddd',
                    }}
                  >
                    {cols.map(col => {
                      const fieldName = col.field.split('.')[1];
                      const value = item[fieldName];
                      const displayName = col.detailHeaderName || addSpacesToCamelCase(fieldName);
                      
                      if (value === undefined || value === null || value === '') return null;
                      
                      return (
                        <Mui.Typography key={col.field} variant="body2" sx={{ mb: 0.5 }}>
                          <strong>{displayName}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </Mui.Typography>
                      );
                    })}
                  </Mui.Box>
                ))}
              </Mui.Box>
            </Mui.Box>
          );
        })}
      </Mui.Box>
    );
  }, [nestedFieldColumns, theme]);

  const handleFilterChanges = (updatedFilters) => {
    if (!builtColumns || builtColumns.length === 0) return;

    const resolvedFilters = typeof updatedFilters === "function" ? updatedFilters(filterModel) : updatedFilters;
    const filterMap = new Map();

    (Array.isArray(resolvedFilters) ? resolvedFilters : []).forEach((filter) => {
      let value = filter.value;
      if (Array.isArray(value)) {
        value = [...value];
      }

      const col = builtColumns.find((col) => col.id === filter.id);
      if (!col) return;

      const filterType = filter.filterType || col?.filterType || "text";
      const hasValue = value !== undefined && value !== null && value !== '' &&
        (!Array.isArray(value) || value.length > 0) &&
        (!Array.isArray(value) || value.some(v => v !== null && v !== undefined && v !== ''));

      if (hasValue) {
        filterMap.set(filter.id, {
          ...filter,
          value,
          filterType,
          operator: getOperatorForFilterType(filterType),
        });
      }
    });

    setFilterModel(Array.from(filterMap.values()));
  };
  
  // Focus on first/last cell to fix accessibility within MRT
  const rowVirtualizerInstanceRef = useRef(null);
  const columnVirtualizerInstanceRef = useRef(null);
  const mrtTable = useRef(null), canFocusSyncStatus = useRef({ row: false, column: false, needsFocus: false, where: undefined });
  
  function rowVirtualizerOnChange(virtualizer, sync) {
    canFocusSyncStatus.current.row = sync;
    const { row, column, needsFocus } = canFocusSyncStatus.current;
    if (row || column || !needsFocus) { return; }
    
    canFocusSyncStatus.current.needsFocus = false;
    
    let element = mrtTable.current.refs.tableContainerRef.current.querySelectorAll("table tr")[1].firstChild;
    if (canFocusSyncStatus.current.where === "End") { element = mrtTable.current.refs.tableContainerRef.current.querySelector("table").lastElementChild.lastElementChild.previousSibling.lastElementChild; }
    element.focus();
  }
  
  function columnVirtualizerOnChange(virtualizer, sync) {
    canFocusSyncStatus.current.column = sync;
    const { row, column, needsFocus } = canFocusSyncStatus.current;
    if (row || column || !needsFocus) { return; }

    canFocusSyncStatus.current.needsFocus = false;
    
    let element = mrtTable.current.refs.tableContainerRef.current.querySelectorAll("table tr")[1].firstChild;
    if (canFocusSyncStatus.current.where === "End") { element = mrtTable.current.refs.tableContainerRef.current.querySelector("table").lastElementChild.lastElementChild.previousSibling.lastElementChild; }
    element.focus();
  }

  function handleJumpToFirstOrLastCell(event) {
        const isWinCtrlMacMeta = (
            (event.ctrlKey && navigator.platform.toLowerCase().includes('win')) ||
            (event.metaKey && navigator.platform.toLowerCase().includes('mac'))
        );
        
        if (!isWinCtrlMacMeta || !["Home", "End"].includes(event.key)) { return; }

        canFocusSyncStatus.current = { column: true, row: true, needsFocus: true, where: event.key };
        
        if (event.key === "Home") {
            columnVirtualizerInstanceRef.current.scrollToIndex(0, { align: "start" });
            rowVirtualizerInstanceRef.current.scrollToIndex(0, { align: "start" });
        } else if (event.key === "End") {
            rowVirtualizerInstanceRef.current.scrollToIndex(100000, { align: "end" });
            columnVirtualizerInstanceRef.current.scrollToIndex(10000, { align: "end" });
        }
        event.preventDefault();
        event.stopPropagation();
  }
  
  const table = useMaterialReactTable({
        muiTableHeadProps: { onKeyDown: handleJumpToFirstOrLastCell },
        muiTableBodyProps: { onKeyDown: handleJumpToFirstOrLastCell },
        muiExpandAllButtonProps: { onKeyDown: event => { event.key == "Enter" && event.target.click(); } },
        enableStickyHeader: true,
        data: rows,
        columns: builtColumns,
        getRowId: row => row.applicantEmployeeId,
        enableColumnResizing: true,
        layoutMode: "grid",
        enableExpanding: Object.keys(nestedFieldColumns).length > 0,
        renderDetailPanel: Object.keys(nestedFieldColumns).length > 0 ? renderDetailPanel : undefined,
        enableRowSelection: row => {
          const raw = row?.original?.isPendingProcessing;
          return !(raw === true || raw === 'Yes');
        },
        muiSelectCheckboxProps: row => {
          const raw = row?.row?.original?.isPendingProcessing;
          const disabled = raw === true || raw === 'Yes';
          return disabled ? { disabled: true, sx: { opacity: 0.3 } } : {};
        },
        muiTableBodyRowProps: ({ row }) => {
          const raw = row?.original?.isPendingProcessing;
          if (raw === true || raw === 'Yes') {
            return { sx: { opacity: 0.5 } };
          }
          return {};
        },
        enableColumnPinning: true,
        columnResizeMode: "onChange",
        enableColumnOrdering: true,
        enableColumnFilterModes: false,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        enableColumnDragging: true,
        enableGlobalFilter: false,
        enableColumnFilters: true,
        muiFilterTextFieldProps: {
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
            }
          }
        },
        muiFilterCheckboxProps: {
          onKeyDown: (e) => {
            if (e.key === ' ') {
              e.stopPropagation();
            }
          }
        },
        muiFilterAutocompleteProps: {
          onKeyDown: (e) => {
            if (e.key === ' ') {
              e.stopPropagation();
            }
          }
        },
        positionToolbarAlertBanner: "bottom",
        onColumnFiltersChange: handleFilterChanges,
        onPaginationChange: setPaginationModel,
        onSortingChange: setSortModel,
        onRowSelectionChange: onRowSelectionChange,
        renderTopToolbarCustomActions: renderTopToolbarContent,
        renderBottomToolbarCustomActions: () => (
          <Mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Mui.Typography variant="body2">Page Size:</Mui.Typography>
            <Mui.TextField
              label="Page Size"
              type="number"
              size="small"
              value={pageSizeInput}
              onChange={(e) => setPageSizeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const value = parseInt(e.target.value, 10);
                  if (value > 0 && value !== pageSizeInput) {
                    setPaginationModel({ ...paginationModel, pageSize: value, pageIndex: 0 });
                  }
                }
              }}
              onBlur={(e) => {
                const value = parseInt(e.target.value, 10);
                if (value > 0 && value !== pageSizeInput) {
                  setPaginationModel({ ...paginationModel, pageSize: value, pageIndex: 0 });
                }
              }}
              inputProps={{ min: 1, max: 1000 }}
              sx={{ width: 100 }}
            />
          </Mui.Box>
        ),
        enableRowVirtualization: true,
        enableColumnVirtualization: true,
        rowVirtualizerOptions: { onChange: rowVirtualizerOnChange },
        rowVirtualizerInstanceRef,
        columnVirtualizerOptions: { onChange: columnVirtualizerOnChange },
        columnVirtualizerInstanceRef,
        muiTableContainerProps: { sx: { maxHeight: '600px' } },
        muiTableBodyCellProps: ({cell}) => ({ sx: sharedTableStyles, "aria-label": cell.getValue() ?? "Empty"}),
        muiTableHeadCellProps: {
          sx: sharedTableStyles,
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            }
          }
        },
        muiTopToolbarProps: { sx: sharedTableStyles },
        muiBottomToolbarProps: { sx: sharedTableStyles },
        rowCount: resultData?.totalCount ?? rows.length,
        state: {
          isFetching,
          rowSelection: selectedRows,
          pagination: paginationModel,
          sorting: sortModel,
          columnFilters: filterModel,
          columnVisibility,
          columnOrder,
        },
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        renderToolbarInternalActions: ({ table }) => (
          <ToolbarInternalActionsWithReset table={table} onResetColumns={resetColumns} onRefresh={onRefresh} canRefresh={canRefresh} />
        ),
    }
  );
  
  mrtTable.current = table;
  
  return (
    <DateFns.LocalizationProvider>
      <MaterialReactTable table={table} />
    </DateFns.LocalizationProvider>
  );
}
